// Minimap-heatmap som visar antingen block-density eller connector-flow.
// Återanvänder `heatmapDensity.ts` (Batch Y) men wrappar med minimap-specifik
// koord-normalisering + opacity-tuning så heatmap:en inte drunknar canvasen.

import { computeDensity, scoreToColor, type DensityGrid, type DensityPoint } from '@/lib/heatmapDensity';

export type HeatmapMode = 'block-density' | 'connector-flow';

export interface MinimapHeatmapInput {
  blocks: { id: string; x: number; y: number }[];
  connectors: { id: string; fromId: string; toId: string }[];
  mode: HeatmapMode;
  /** Grid-storlek. Minimap är liten så 10x10 är bra default. */
  rows?: number;
  cols?: number;
  bounds?: { minX: number; minY: number; maxX: number; maxY: number };
}

export interface MinimapHeatmapOutput {
  grid: DensityGrid;
  mode: HeatmapMode;
  colors: { r: number; g: number; b: number; alpha: number }[][];
}

/**
 * Bygg heatmap från minimap-state. connector-flow lägger vikt på både
 * from- och to-ändpunkter (dubbel-tätt vid node som är mål för många).
 */
export function buildMinimapHeatmap(input: MinimapHeatmapInput): MinimapHeatmapOutput {
  const rows = input.rows ?? 10;
  const cols = input.cols ?? 10;

  const blockById = new Map(input.blocks.map(b => [b.id, b]));
  let points: DensityPoint[];
  if (input.mode === 'connector-flow') {
    points = [];
    for (const c of input.connectors) {
      const from = blockById.get(c.fromId);
      const to = blockById.get(c.toId);
      if (from) points.push({ x: from.x, y: from.y });
      if (to) points.push({ x: to.x, y: to.y });
    }
  } else {
    points = input.blocks.map(b => ({ x: b.x, y: b.y }));
  }

  const grid = computeDensity(points, { rows, cols, bounds: input.bounds });

  // Färg + alpha. Tomma celler = fully transparent så minimap-baseline syns.
  const colors: { r: number; g: number; b: number; alpha: number }[][] =
    grid.cells.map(row => row.map(score => {
      const c = scoreToColor(score);
      return { ...c, alpha: score === 0 ? 0 : 0.3 + 0.5 * score };
    }));

  return { grid, mode: input.mode, colors };
}
